import { oneSpec } from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    FieldGrid,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    StatusBadge,
    statusLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Timeline,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { StaffSession } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { useOne } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';
import {
    SessionCreator,
    SessionCustomer,
    sessionSource,
    sessionType,
    SessionVerdict,
} from './index';

/** The reads of `/sessions/:id` (BM-46 §2.0). */
export const sessionReads = (id: string | undefined) => ({
    session: oneSpec(`/staff/sessions/${id ?? ''}`),
});

type Attempt = StaffSession['attempts'][number];
type Check = NonNullable<Attempt['risk_check']>;

/** A timeline mark per attempt: approved green, declined (by the issuer or by Risk before a charge) red, else neutral. */
const tone = (a: Attempt) =>
    a.state === 'declined' ||
    (a.state === null && a.risk_check?.verdict === 'decline')
        ? ('danger' as const)
        : a.state === 'approved'
          ? ('success' as const)
          : ('neutral' as const);

/**
 * bm-111 QA-9: why Risk allowed or declined an attempt: the verdict, the check's score and every rule hit with the
 * action and score it had in the set the check ran (a dash when that set no longer names the rule).
 */
function RiskCheck({
    check,
    onRules,
}: {
    check: Check;
    onRules: (c: Check) => void;
}) {
    return (
        <span className="flex flex-col gap-0.5" data-testid="attempt-risk">
            <span>
                Risk: {statusLabel(check.verdict)} ({statusLabel(check.stage)})
                · score {check.score}
            </span>
            {check.rules.length ? (
                check.rules.map((r, i) => (
                    <span
                        key={`${r.name}-${i}`}
                        className="text-muted-foreground text-xs"
                        data-testid="attempt-risk-rule"
                    >
                        {r.name} · {r.action ? statusLabel(r.action) : '—'} ·
                        score {r.score ?? '—'}
                        {/* BM-125: the engine's explanation of the hit, rendered from the observed values */}
                        {r.explanation ? ` · ${r.explanation}` : ''}
                    </span>
                ))
            ) : (
                <span className="text-muted-foreground text-xs">
                    No rule fired
                </span>
            )}
            <Button
                size="sm"
                variant="outline"
                className="mt-1 self-start"
                aria-label={`View the rules of check ${check.id}`}
                onClick={() => onRules(check)}
            >
                View rules
            </Button>
        </span>
    );
}

const names = (rules: readonly { name: string }[]) =>
    rules.map((r) => r.name).join(', ');

/**
 * QA round 8: why the check answered what it did, in one sentence, from the rule RiskCheckService::evaluate applies:
 * of the rules the stage runs, any fired decline rule declines, else any fired review rule sends to review, else the
 * check allows; the score is the sum of the fired rules' scores.
 */
function riskExplanation(check: Check): string {
    const fired = (action: string) =>
        check.rules.filter((r) => r.action === action);
    if (check.verdict === 'decline') {
        const by = fired('decline');
        return by.length
            ? `Declined because the decline rule ${names(by)} fired; a fired decline rule always declines.`
            : 'Declined by a rule the rule set the check ran no longer names.';
    }
    if (check.verdict === 'review') {
        const by = fired('review');
        return by.length
            ? `Sent to review because the review rule ${names(by)} fired and no decline rule fired.`
            : 'Sent to review by a rule the rule set the check ran no longer names; no decline rule fired.';
    }
    return check.rules.length
        ? `Allowed because no decline or review rule fired; ${names(check.rules)} fired and added ${check.score} to the score.`
        : 'Allowed because no rule fired.';
}

/** QA round 8: every rule of the set the check ran, fired or not, with its stage, action and score. */
function RuleSetSheet({
    check,
    onClose,
}: {
    check: Check | null;
    onClose: () => void;
}) {
    return (
        <Sheet
            open={check !== null}
            onOpenChange={(o) => (o ? null : onClose())}
        >
            <SheetContent
                side="right"
                className="w-full overflow-y-auto p-0 sm:max-w-2xl"
                data-testid="risk-rules-sheet"
            >
                <SheetTitle className="sr-only">Risk check rules</SheetTitle>
                <SheetDescription className="sr-only">
                    Every rule of the rule set the check ran, fired or not
                </SheetDescription>
                {check ? (
                    <div className="flex flex-col gap-5 p-5">
                        <div>
                            <h2 className="text-fg-1 text-20 m-0 font-semibold">
                                Risk check ({statusLabel(check.stage)})
                            </h2>
                            <p className="text-fg-2 mt-1 mb-0 text-sm">
                                {statusLabel(check.verdict)} · score{' '}
                                {check.score}
                            </p>
                        </div>
                        <p
                            className="m-0 text-sm"
                            data-testid="risk-explanation"
                        >
                            {riskExplanation(check)}
                        </p>
                        {check.rule_set === null ? (
                            <p
                                className="text-fg-2 m-0 text-sm"
                                data-testid="risk-rules-unknown"
                            >
                                The rule set this check ran is no longer stored,
                                so only the rules that fired are known
                                {check.rules_hit.length
                                    ? `: ${check.rules_hit.join(', ')}.`
                                    : '.'}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Rule</TableHead>
                                            <TableHead>Stage</TableHead>
                                            <TableHead>Action</TableHead>
                                            <TableHead>Score</TableHead>
                                            <TableHead>Result</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {check.rule_set.map((r, i) => (
                                            <TableRow
                                                key={`${r.name}-${i}`}
                                                data-testid="risk-rule"
                                                data-fired={String(r.fired)}
                                            >
                                                <TableCell>{r.name}</TableCell>
                                                <TableCell>
                                                    {statusLabel(r.stage)}
                                                </TableCell>
                                                <TableCell>
                                                    {statusLabel(r.action)}
                                                </TableCell>
                                                <TableCell className="tabular-nums">
                                                    {r.score}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        r.fired
                                                            ? 'font-semibold'
                                                            : 'text-fg-2'
                                                    }
                                                >
                                                    {r.fired
                                                        ? 'Fired'
                                                        : 'Not fired'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                ) : null}
            </SheetContent>
        </Sheet>
    );
}

/**
 * One attempt of the timeline: its charge (linked to the charge page), state, decline category and risk check; a
 * checked attempt with no charge (Risk refused it, or Risk allowed it and Payments refused or failed) has no charge
 * to link (QA-9).
 */
function attemptItem(a: Attempt, onRules: (c: Check) => void) {
    return {
        id: a.charge_id ?? `check-${a.risk_check?.id ?? a.attempt}`,
        at: a.created_at,
        tone: tone(a),
        title: (
            <span className="flex flex-wrap items-center gap-2">
                <span>
                    {a.attempt === null ? 'Attempt' : `Attempt ${a.attempt}`}
                </span>
                {a.state ? (
                    <StatusBadge status={a.state} domain="charge" />
                ) : a.risk_check?.verdict === 'decline' ? (
                    <span className="text-destructive text-xs font-semibold">
                        Refused by risk, no charge
                    </span>
                ) : (
                    // spec bm-111 § 10: a check Risk allowed or sent to review with no charge (Payments refused it or
                    // was down, SessionService::complete) reads its own verdict, never "refused by risk"
                    <span className="text-xs font-semibold">
                        No charge
                        {a.risk_check
                            ? ` (Risk: ${statusLabel(a.risk_check.verdict)})`
                            : ''}
                    </span>
                )}
                <Money cents={a.amount_cents} currency={a.currency} />
            </span>
        ),
        detail: (
            <span className="flex flex-col gap-1">
                {a.charge_id ? (
                    <Link
                        to={`/money/charges/${encodeURIComponent(a.charge_id)}`}
                        className="font-mono text-xs hover:underline"
                    >
                        {a.charge_id}
                    </Link>
                ) : null}
                {a.decline_category ? (
                    <span>
                        Decline reason: {statusLabel(a.decline_category)}
                    </span>
                ) : null}
                {a.risk_check ? (
                    <RiskCheck check={a.risk_check} onRules={onRules} />
                ) : (
                    <span>Risk: no check recorded</span>
                )}
            </span>
        ),
    };
}

/**
 * bm-111 § 10: one session as staff read it (§ 9): its facts, every signal collected from the buyer (raw address and
 * browser string included, staff only) and a vertical timeline of its attempts, each linking to its charge page.
 */
export function SessionPage() {
    const { id = '' } = useParams();
    const query = useOne<StaffSession>(sessionReads(id).session.path);
    const [rulesOf, setRulesOf] = useState<Check | null>(null);
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Sessions', href: '/sessions' }]}
                {...detailTitle(query, null, id, 'Session')}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Session">
                {(s) => (
                    <div className="space-y-4">
                        <Card data-testid="session-facts">
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Session
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <FieldGrid>
                                    <DetailRow label="State" layout="stacked">
                                        <StatusBadge
                                            status={s.state}
                                            domain={ADMIN_STATUSES.session}
                                        />
                                    </DetailRow>
                                    <DetailRow label="Amount" layout="stacked">
                                        {s.amount_cents === null ? (
                                            '—'
                                        ) : (
                                            <Money
                                                cents={s.amount_cents}
                                                currency="USD"
                                            />
                                        )}
                                    </DetailRow>
                                    <DetailRow
                                        label="Risk result"
                                        layout="stacked"
                                    >
                                        <SessionVerdict
                                            verdict={s.risk_verdict}
                                        />
                                    </DetailRow>
                                    <DetailRow
                                        label="Attempts"
                                        layout="stacked"
                                    >
                                        {s.attempt}
                                    </DetailRow>
                                    <DetailRow
                                        label="Merchant"
                                        layout="stacked"
                                    >
                                        <ObjectRef id={s.merchant_id} />
                                    </DetailRow>
                                    <DetailRow
                                        label="Customer"
                                        layout="stacked"
                                    >
                                        <SessionCustomer session={s} />
                                    </DetailRow>
                                    <DetailRow
                                        label="Billing address"
                                        layout="stacked"
                                    >
                                        {s.billing_address
                                            ? [
                                                  s.billing_address.line1,
                                                  s.billing_address.line2,
                                                  s.billing_address.city,
                                                  `${s.billing_address.state} ${s.billing_address.postal_code}`,
                                                  s.billing_address.country,
                                              ]
                                                  .filter(Boolean)
                                                  .join(', ')
                                            : '—'}
                                    </DetailRow>
                                    <DetailRow label="Type" layout="stacked">
                                        <span data-testid="session-type">
                                            {sessionType(s)}
                                        </span>
                                        {s.mode === 'setup' &&
                                        s.setup_subscription_public_id ? (
                                            <ObjectRef
                                                id={
                                                    s.setup_subscription_public_id
                                                }
                                            />
                                        ) : null}
                                    </DetailRow>
                                    <DetailRow label="Source" layout="stacked">
                                        <span data-testid="session-source">
                                            {sessionSource(s.source)}
                                        </span>
                                    </DetailRow>
                                    <DetailRow
                                        label="Created by"
                                        layout="stacked"
                                    >
                                        <SessionCreator
                                            creator={s.created_by}
                                        />
                                    </DetailRow>
                                    <DetailRow label="Created" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={s.created_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                    <DetailRow
                                        label="Completed"
                                        layout="stacked"
                                    >
                                        <DateTime
                                            zone="inline"
                                            value={s.completed_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                </FieldGrid>
                            </CardContent>
                        </Card>
                        <Card data-testid="session-signals">
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Collected signals
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <FieldGrid>
                                    <DetailRow
                                        label="IP address"
                                        layout="stacked"
                                    >
                                        <span className="font-mono">
                                            {s.signals.client_ip ?? '—'}
                                        </span>
                                    </DetailRow>
                                    <DetailRow
                                        label="Network country"
                                        layout="stacked"
                                    >
                                        {s.signals.client_country ?? '—'}
                                    </DetailRow>
                                    <DetailRow label="Device" layout="stacked">
                                        {s.signals.device_class
                                            ? statusLabel(
                                                  s.signals.device_class,
                                              )
                                            : '—'}
                                    </DetailRow>
                                    <DetailRow
                                        label="Language"
                                        layout="stacked"
                                    >
                                        {s.signals.browser_language ?? '—'}
                                    </DetailRow>
                                    <DetailRow
                                        label="Timezone"
                                        layout="stacked"
                                    >
                                        {s.signals.browser_timezone ?? '—'}
                                    </DetailRow>
                                    <DetailRow
                                        label="First opened"
                                        layout="stacked"
                                    >
                                        <DateTime
                                            zone="inline"
                                            value={s.signals.first_opened_at}
                                            empty="—"
                                        />
                                    </DetailRow>
                                    <DetailRow label="Browser" layout="stacked">
                                        <span className="break-all">
                                            {s.signals.client_user_agent ?? '—'}
                                        </span>
                                    </DetailRow>
                                </FieldGrid>
                            </CardContent>
                        </Card>
                        <Card data-testid="session-attempts">
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Attempts
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Timeline
                                    items={s.attempts.map((a) =>
                                        attemptItem(a, setRulesOf),
                                    )}
                                    empty="No payment was attempted on this session."
                                />
                                <RuleSetSheet
                                    check={rulesOf}
                                    onClose={() => setRulesOf(null)}
                                />
                            </CardContent>
                        </Card>
                    </div>
                )}
            </DetailBody>
        </div>
    );
}
